import React, { useState, useEffect } from 'react';
import './App.css';

function App() {
  const [articles, setArticles] = useState([]);
  const [breakingNews, setBreakingNews] = useState([]);
  const [selectedLeague, setSelectedLeague] = useState('Football General');
  const [loading, setLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [showTerms, setShowTerms] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [language, setLanguage] = useState('EN');

const getApiBase = () => {
  return 'https://sportmobile-api-kv6p4mk5ea-uc.a.run.app/api';
};

  const API_BASE = getApiBase();

  const leagues = [
    'Football General',
    'Premier League',
    'La Liga',
    'Bundesliga',
    'Serie A',
    'Ligue 1'
  ];

  // ✅ FIX: Smart image loader
  // Guardian/BBC/ESPN block server proxy - load them directly with no-referrer
  const getImageSrc = (imageUrl) => {
    if (!imageUrl) return null;
    const directDomains = [
      'guim.co.uk',
      'theguardian.com',
      'bbci.co.uk',
      'bbc.co.uk',
      'espncdn.com',
      'espn.com'
    ];
    const isDirect = directDomains.some(domain => imageUrl.includes(domain));
    if (isDirect) return imageUrl;
    return `${API_BASE}/proxy-image?url=${encodeURIComponent(imageUrl)}`;
  };

  const cleanText = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    let text = tmp.textContent || tmp.innerText || '';
    text = text.replace(/\s*Source\s*$/gi, '');
    text = text.replace(/\s*Source:\s*/gi, '');
    text = text.replace(/\.\.\.\.\s*Source/gi, '...');
    return text.trim();
  };

  const t = {
    EN: {
      home: 'Home', articles: 'Articles', terms: 'Terms & Conditions',
      latestNews: 'Latest News', topStories: 'Top Stories', highlights: 'Highlights',
      moreInCategory: 'More articles in category', noArticles: 'No articles found',
      runFetcher: 'Run the RSS fetcher to add articles!', loading: 'Loading articles...',
      readMore: 'Read More', breakingNews: '🔥 BREAKING NEWS',
      readFullStory: 'Read Full Story', copyright: '© 2026 SportMobile. All rights reserved.'
    },
    BG: {
      home: 'Начало', articles: 'Статии', terms: 'Условия и правила',
      latestNews: 'Последни новини', topStories: 'Топ статии', highlights: 'Акценти',
      moreInCategory: 'Още статии в категория', noArticles: 'Няма намерени статии',
      runFetcher: 'Стартирайте RSS fetcher за да добавите статии!', loading: 'Зареждане на статии...',
      readMore: 'Прочети повече', breakingNews: '🔥 ГОРЕЩИ НОВИНИ',
      readFullStory: 'Прочети цялата история', copyright: '© 2026 SportMobile. Всички права запазени.'
    }
  };

  useEffect(() => {
    fetch(`${API_BASE}/breaking`)
      .then(res => res.json())
      .then(data => { if (data.success) setBreakingNews(data.articles || []); })
      .catch(err => console.error('Error:', err));
  }, [API_BASE]);

  useEffect(() => {
    setLoading(true);
    const url = selectedLeague === 'Football General'
      ? `${API_BASE}/highlights`
      : `${API_BASE}/articles?league=${encodeURIComponent(selectedLeague)}`;
    fetch(url)
      .then(res => res.json())
      .then(data => { if (data.success) setArticles(data.articles || []); setLoading(false); })
      .catch(err => { console.error('Error:', err); setLoading(false); });
  }, [selectedLeague, API_BASE]);

  useEffect(() => {
    if (breakingNews.length > 0) {
      const timer = setInterval(() => setCurrentSlide(prev => (prev + 1) % breakingNews.length), 5000);
      return () => clearInterval(timer);
    }
  }, [breakingNews]);

  return (
    <div className="App">
      {/* HEADER */}
      <header className="header">
        <div className="header-content">
          <div className="logo">
            <img src="/sportsmobile.png" alt="Sports Mobile Logo" className="logo-image"
              style={{height: '50px', width: 'auto'}}
              onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.querySelector('.logo-fallback').style.display = 'flex'; }}
            />
            <div className="logo-fallback" style={{display: 'none'}}>
              <span className="logo-icon">⚽</span>
              <span className="logo-text">SPORTS MOBILE</span>
            </div>
          </div>
          <nav className="nav">
            <button className="nav-link" onClick={() => window.location.reload()}>{t[language].home}</button>
            <button className="nav-link">{t[language].articles}</button>
            <button className="nav-link" onClick={() => setShowTerms(true)}>{t[language].terms}</button>
            <div className="language-selector">
              <button className={`language-btn ${language === 'EN' ? 'active' : ''}`} onClick={() => setLanguage('EN')}>EN</button>
              <button className={`language-btn ${language === 'BG' ? 'active' : ''}`} onClick={() => setLanguage('BG')}>BG</button>
            </div>
          </nav>
        </div>
      </header>

      {/* BREAKING NEWS SLIDER */}
      {breakingNews.length > 0 && (
        <div className="breaking-news-slider">
          {breakingNews[currentSlide]?.image_url && (
            <div className="slider-background"
              style={{ backgroundImage: `url(${getImageSrc(breakingNews[currentSlide].image_url)})` }}
            />
          )}
          <div className="slider-overlay"></div>
          <div className="slider-content">
            <div className="slider-badge">{t[language].breakingNews}</div>
            <h1 className="slider-title">{breakingNews[currentSlide]?.title}</h1>
            <p className="slider-description">
              {cleanText(breakingNews[currentSlide]?.description || '').substring(0, 200)}...
            </p>
            <button className="slider-button" onClick={() => setSelectedArticle(breakingNews[currentSlide])}>
              {t[language].readFullStory} →
            </button>
            <div className="slider-dots">
              {breakingNews.map((_, index) => (
                <button key={index} className={`slider-dot ${currentSlide === index ? 'active' : ''}`}
                  onClick={() => setCurrentSlide(index)} />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* LEAGUE FILTER */}
      <div className="league-filter">
        <div className="league-filter-content">
          {leagues.map(league => (
            <button key={league} className={`league-btn ${selectedLeague === league ? 'active' : ''}`}
              onClick={() => setSelectedLeague(league)}>
              {league}
            </button>
          ))}
        </div>
      </div>

      {/* ARTICLES GRID */}
      <div className="news-grid">
        <h2 className="news-grid-title">
          {selectedLeague === 'Football General' ? t[language].highlights : `${selectedLeague} - ${t[language].latestNews}`}
        </h2>
        {loading ? (
          <div className="loading"><div className="loading-spinner"></div><p>{t[language].loading}</p></div>
        ) : articles.length === 0 ? (
          <div className="no-articles">
            <div className="no-articles-icon">🌐</div>
            <h3>{t[language].noArticles}</h3>
            <p>{t[language].runFetcher}</p>
          </div>
        ) : (
          <div className="articles-grid">
            {articles.map(article => (
              <div key={article.id} className="article-card" onClick={() => setSelectedArticle(article)}>
                <div className="article-image-container">
                  {article.image_url ? (
                    // ✅ referrerPolicy="no-referrer" fixes Guardian & BBC blocking
                    <img
                      src={getImageSrc(article.image_url)}
                      alt={article.title}
                      className="article-image"
                      referrerPolicy="no-referrer"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.querySelector('.article-placeholder').style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div className="article-placeholder" style={{display: article.image_url ? 'none' : 'flex'}}>⚽</div>
                  {article.league && <div className="article-league-badge">{article.league}</div>}
                </div>
                <div className="article-content">
                  <h3 className="article-title">{article.title}</h3>
                  {article.description && <p className="article-description">{cleanText(article.description)}</p>}
                  <div className="article-meta">
                    <span className="article-date">
                      {article.pub_date ? new Date(article.pub_date).toLocaleDateString(language === 'BG' ? 'bg-BG' : 'en-US') : 'Recent'}
                    </span>
                    <button className="read-more-btn">{t[language].readMore} →</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ARTICLE MODAL */}
      {selectedArticle && !showTerms && (
        <div className="modal-overlay" onClick={() => setSelectedArticle(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedArticle(null)}>×</button>
            {selectedArticle.image_url && (
              // ✅ referrerPolicy="no-referrer" fixes Guardian & BBC in modal too
              <img
                src={getImageSrc(selectedArticle.image_url)}
                alt={selectedArticle.title}
                className="article-modal-image"
                referrerPolicy="no-referrer"
                onError={e => e.target.style.display = 'none'}
              />
            )}
            <div className="modal-body">
              <h2 className="modal-title">{selectedArticle.title}</h2>
              <div className="modal-meta">
                {selectedArticle.league && <div className="modal-meta-item"><span>🏆</span><span>{selectedArticle.league}</span></div>}
                {selectedArticle.pub_date && (
                  <div className="modal-meta-item">
                    <span>📅</span>
                    <span>{new Date(selectedArticle.pub_date).toLocaleDateString(language === 'BG' ? 'bg-BG' : 'en-US')}</span>
                  </div>
                )}
              </div>
              {selectedArticle.description && <div className="modal-description"><p>{cleanText(selectedArticle.description)}</p></div>}
              {selectedArticle.content && <div className="modal-content-text"><p>{cleanText(selectedArticle.content)}</p></div>}
            </div>
          </div>
        </div>
      )}

      {/* TERMS MODAL */}
      {showTerms && (
        <div className="modal-overlay" onClick={() => setShowTerms(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowTerms(false)}>×</button>
            <div className="modal-body">
              {language === 'EN' ? (
                <>
                  <h2 className="modal-title">Terms & Conditions - English</h2>
                  <div className="terms-section"><h3>I. Prices and Payment</h3>
                    <p><strong>1.</strong> The service offers a basic payment method: via DCB (payment that deducts the amount from your mobile phone bill).</p>
                    <p><strong>2.</strong> Amounts for payments made are not refundable.</p>
                    <p><strong>3.</strong> The service is based on a weekly subscription and costs 6.01 lv / 3.07 EUR with VAT for Yettel and Vivacom users. The service is based on a monthly subscription and costs 6.01 lv / 3.07 EUR with VAT for A1 users, upon payment of the amount deducted from the mobile phone bill.</p>
                    <p><strong>4.</strong> The service is valid for the following mobile operators: A1, Vivacom and Yettel. Enter your mobile phone number and confirm with the received PIN to activate the service.</p>
                  </div>
                  <div className="terms-section"><h3>II. Cancellation</h3>
                    <p>The right of cancellation by the User may cancel the subscription by sending an SMS with the text STOP SPORT to the short number 14703 (The message is charged depending on your tariff model).</p>
                  </div>
                  <div className="terms-section"><h3>Article 1 - Rights and Responsibilities</h3>
                    <p>The terms and conditions mentioned on the website apply to and are part of all offers for digital and other services offered online by DIGITALIZE HUB LLC through mobile communication network operators with which the end user has concluded a subscription.</p>
                  </div>
                  <div className="terms-section"><h3>Article 2 - Price and Product Changes</h3>
                    <p><strong>2.1</strong> Existing end users will be notified via SMS one week before the price adjustment is implemented.</p>
                    <p><strong>2.2</strong> DIGITALIZE HUB LLC reserves the right to replace any product with an equivalent alternative product without prior notice.</p>
                  </div>
                  <div className="terms-section"><h3>Article 3 - Costs</h3>
                    <p><strong>3.1</strong> Offers or quotas indicated in advertisements are non-binding, unless expressly stated otherwise.</p>
                    <p><strong>3.2</strong> The Operator charges the costs or reduces the amount of prepaid subscriptions.</p>
                    <p><strong>3.3</strong> Costs may be charged for each message sent or received depending on the type of message and/or payment method.</p>
                  </div>
                  <div className="terms-section"><h3>Article 4 - Provision of Content Services</h3>
                    <p>The Content Service is required by DIGITALIZE HUB LLC and is provided to the end user by the Operator's service and network.</p>
                  </div>
                  <div className="terms-section"><h3>Article 5 - Termination</h3>
                    <p>The methods for terminating the Content Delivery Service are listed in the General Terms and Conditions of Use.</p>
                  </div>
                  <div className="terms-section"><h3>Article 6 - Intellectual Property</h3>
                    <p><strong>6.1</strong> All copyrights, patents, trademarks, designs and other intellectual property rights apply to the content and/or services.</p>
                    <p><strong>6.2</strong> DIGITALIZE HUB LLC grants the end user a limited, non-exclusive, non-transferable right to use the content services.</p>
                  </div>
                  <div className="terms-section"><h3>Article 7 - Data Processing and Privacy</h3>
                    <p><strong>7.1</strong> DIGITALIZE HUB LLC reserves the right to use all data of the End User for additional promotional purposes.</p>
                    <p><strong>7.2</strong> DIGITALIZE HUB LLC collects and processes certain personal data and internet traffic data.</p>
                    <p><strong>7.3</strong> Data processing complies with relevant privacy laws and regulations.</p>
                  </div>
                  <div className="terms-section"><h3>Article 8 - Limitation of Liability</h3>
                    <p><strong>8.1</strong> The end user bears the risk of using the content services.</p>
                    <p><strong>8.2</strong> Connection quality may vary by time and location.</p>
                    <p><strong>8.3</strong> No refunds for service interruptions.</p>
                    <p><strong>8.4</strong> DIGITALIZE HUB LLC is not liable for hardware or software damage.</p>
                    <p><strong>8.5</strong> Compensation shall never exceed the amount of the bill for content services.</p>
                  </div>
                  <div className="terms-section"><h3>Article 9 - Compensation</h3>
                    <p>The End User must not send infected, illegal, harmful, or obscene messages.</p>
                  </div>
                  <div className="terms-section"><h3>Article 10 - Service Orders</h3>
                    <p>The End User must be 18 years of age or older. Consent from bill payer required for minors.</p>
                  </div>
                  <div className="terms-section"><h3>Article 11 - Applicable Laws</h3>
                    <p><strong>11.1</strong> The law of the country in which the campaign is held shall be the applicable law.</p>
                    <p><strong>11.2</strong> The courts of Cyprus shall have exclusive jurisdiction.</p>
                  </div>
                  <div className="terms-section"><h3>Article 12 - Amendments</h3>
                    <p>DIGITALIZE HUB LLC may amend these Terms and Conditions from time to time. End Users will be informed via the Website.</p>
                    <p><strong>DIGITALIZE HUB LLC</strong> is headquartered in the State of Delaware and is registered with the Chamber of Commerce under number 3532904.</p>
                  </div>
                </>
              ) : (
                <>
                  <h2 className="modal-title">Условия и правила - България</h2>
                  <div className="terms-section"><h3>I. Цени и заплащане</h3>
                    <p><strong>1.</strong> Услугата предлага основен метод на плащане: чрез DCB (плащане, което приспада сумата от сметката ви за мобилен телефон).</p>
                    <p><strong>2.</strong> Суми за извършени плащания не се връщат.</p>
                    <p><strong>3.</strong> Услугата е на база седмичен абонамент и е на цена 6.01 lv / 3.07 EUR с ДДС за потребители на Yettel и Vivacom. Услугата е на база месечен абонамент, а цената й е 6.01 lv / 3.07 EUR с ДДС за потребители на А1.</p>
                    <p><strong>4.</strong> Услугата е валидна за следните мобилни оператори: A1, Vivacom и Yettel. Въведете номера на мобилния си телефон и потвърдете с получения ПИН.</p>
                  </div>
                  <div className="terms-section"><h3>II. Отказ</h3>
                    <p>Правото на отказ от страна на Потребителя може да се откаже от абонамента като изпрати СМС с текст STOP SPORT на кратък номер 14703 (Съобщението се таксува зависимо от тарифния модел).</p>
                  </div>
                  <div className="terms-section"><h3>Член 1 - Права и отговорности</h3>
                    <p>Правилата и условията се прилагат за всички оферти за цифрови и други услуги, предлагани онлайн от DIGITALIZE HUB LLC чрез мрежови оператори за мобилна комуникация.</p>
                  </div>
                  <div className="terms-section"><h3>Член 2 - Промяна на цени и продукти</h3>
                    <p><strong>2.1</strong> Съществуващите крайни потребители ще бъдат уведомени чрез SMS една седмица преди корекцията на цените.</p>
                    <p><strong>2.2</strong> DIGITALIZE HUB LLC си запазва правото да замени всеки продукт с еквивалентен алтернативен продукт.</p>
                  </div>
                  <div className="terms-section"><h3>Член 3 - Разноски</h3>
                    <p><strong>3.1</strong> Офертите са необвързващи, освен ако изрично не е посочено друго.</p>
                    <p><strong>3.2</strong> Операторът начислява разходите или намалява сумата на предплатените абонаменти.</p>
                    <p><strong>3.3</strong> Разходите могат да бъдат таксувани за всяко изпратено или получено съобщение.</p>
                  </div>
                  <div className="terms-section"><h3>Член 4 - Предоставяне на услуги със съдържание</h3>
                    <p>Услугата за съдържание се изисква от DIGITALIZE HUB LLC и се предоставя от услугата и мрежата на Оператора.</p>
                  </div>
                  <div className="terms-section"><h3>Член 5 - Прекратяване и прекратяване на договора</h3>
                    <p>Методите за прекратяване на услугата са изброени в Общите условия на потребителя.</p>
                  </div>
                  <div className="terms-section"><h3>Член 6 - Интелектуална собственост</h3>
                    <p><strong>6.1</strong> Всички авторски права, патенти, търговски марки се прилагат за съдържанието и услугите.</p>
                    <p><strong>6.2</strong> DIGITALIZE HUB LLC предоставя ограничено, неизключително право за използване на услугите.</p>
                  </div>
                  <div className="terms-section"><h3>Член 7 - Обработка на данни и поверителност</h3>
                    <p><strong>7.1</strong> DIGITALIZE HUB LLC си запазва правото да използва данните за промоционални цели.</p>
                    <p><strong>7.2</strong> DIGITALIZE HUB LLC събира и обработва лични данни и данни за трафика.</p>
                    <p><strong>7.3</strong> Обработката на данни спазва законите за поверителност.</p>
                  </div>
                  <div className="terms-section"><h3>Член 8 - Ограничение на отговорността</h3>
                    <p><strong>8.1</strong> Крайният потребител носи риска от използването на услугите.</p>
                    <p><strong>8.2</strong> Качеството на връзката може да варира според времето и местоположението.</p>
                    <p><strong>8.3</strong> Няма право на възстановяване при прекъсване на услугата.</p>
                    <p><strong>8.4</strong> DIGITALIZE HUB LLC не носи отговорност за щети на хардуер или софтуер.</p>
                    <p><strong>8.5</strong> Обезщетението няма да надвишава сумата на сметката за услугите.</p>
                  </div>
                  <div className="terms-section"><h3>Член 9 - Обезщетение</h3>
                    <p>Крайният потребител не може да изпраща заразени, незаконни или вредни съобщения.</p>
                  </div>
                  <div className="terms-section"><h3>Член 10 - Поръчки за обслужване</h3>
                    <p>Крайният потребител трябва да е навършил 18 години. Изисква се съгласие от платеца за непълнолетни.</p>
                  </div>
                  <div className="terms-section"><h3>Член 11 - Приложими закони</h3>
                    <p><strong>11.1</strong> Законът на държавата, в която се провежда кампанията, е приложимият закон.</p>
                    <p><strong>11.2</strong> Съдът в Кипър има изключителна юрисдикция.</p>
                  </div>
                  <div className="terms-section"><h3>Член 12 - Изменения</h3>
                    <p>DIGITALIZE HUB LLC може от време на време да изменя тези Общи условия. Крайните потребители ще бъдат информирани чрез уебсайта.</p>
                    <p><strong>DIGITALIZE HUB LLC</strong> е със седалище в щата Делауеър и е регистрирано под номер 3532904.</p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* FOOTER */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-logo-section">
            <img src="/sportsmobile.png" alt="Sports Mobile Logo" className="footer-logo"
              style={{height: '60px', width: 'auto', marginBottom: '10px'}}
              onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.querySelector('.footer-logo-fallback').style.display = 'flex'; }}
            />
            <div className="footer-logo-fallback" style={{display: 'none'}}>
              <span style={{fontSize: '2rem'}}>⚽</span>
              <span style={{fontSize: '1.2rem', fontWeight: 'bold', marginLeft: '0.5rem'}}>SPORTS MOBILE</span>
            </div>
          </div>
          <p className="footer-bottom">{t[language].copyright}</p>
        </div>
      </footer>
    </div>
  );
}

export default App;


